/**
 * N3 Anfo-Qualität je Quelle (Workshop-Lücken 29.10.2026, Plan `.claude/plans/2026-10-29-workshop-luecken.md` Paket U,
 * Q3 Till): Bezahlquote reifer Anfos (60–240 Tage alt) je Produkt, Kanalgruppe oder Webinar, als Hantel Vergleich ○ →
 * jetzt ●. Produkt/Kanal gegen dieselben Tage ein Jahr früher, Webinar gegen den Rest seines Produkts ohne dieses Webinar.
 * ▲/▼ nur nach Holm über alle testbaren Zeilen des Reiters; unter der Schwelle grau, ohne Marke.
 * Backend `/api/auswertungen/umsatz/qualitaet-je-quelle?dim=` (`qualitaetService.ts`).
 */
import { FC, useEffect, useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ChartCard from '../../../components/auswertungen/ChartCard';
import SortableTable, { Spalte } from '../../../components/auswertungen/SortableTable';
import { AbweichungMarke, BefundZeile, Hinweis, int, Laden, spanne, useBreitePx } from '../../../components/auswertungen/kennzahlTeile';
import { useVizStyle } from '../../../components/auswertungen/vizStyle';
import { formatPct, TERMS } from '../../../helpers/kpiDefinitions';
import { fetchQualitaetQuelle, type QualitaetDimension, type QualitaetQuelleAntwort, type QualitaetQuelleZeile } from '../../../helpers/umsatzData';
import { DatenstandText, Kartenkopf, letzterTag } from './teile';
import { ppText } from '../kanaele/befund';

const REITER: { key: QualitaetDimension; label: string }[] = [
  { key: 'produkt', label: 'Produkt' },
  { key: 'kanalgruppe', label: 'Kanal' },
  { key: 'webinar', label: 'Webinar' },
];
/** Unter dieser Kartenbreite stehen Vergleich und Unterschied als Unterzeilen. */
const SCHMAL_UNTER = 620;

/** Achsenende der Hantel: nächste 10 über der höchsten Quote, mindestens 40 %. */
export function hantelEnde(rows: Pick<QualitaetQuelleZeile, 'pay_rate' | 'pay_rate_basis'>[]): number {
  const max = Math.max(40, ...rows.flatMap((r) => [r.pay_rate ?? 0, r.pay_rate_basis ?? 0]));
  return Math.min(100, Math.ceil(max / 10) * 10);
}

const pp = (v: number | null) =>
  v === null ? '—' : `${v > 0 ? '+' : v < 0 ? '−' : '±'}${Math.abs(v).toLocaleString('de-DE', { maximumFractionDigits: 1, minimumFractionDigits: 1 })} Pp`;

const Hantel: FC<{ r: QualitaetQuelleZeile; ende: number; grau: boolean; schmal?: boolean }> = ({ r, ende, grau, schmal }) => {
  const viz = useVizStyle();
  const x = (v: number) => `${Math.max(0, Math.min(100, (v / ende) * 100))}%`;
  const farbe = grau ? viz.muted : r.test?.flag === 'up' ? '#2e7d32' : r.test?.flag === 'down' ? '#c62828' : viz.series1;
  const a = r.pay_rate;
  const b = r.pay_rate_basis;
  return (
    <Box sx={{ position: 'relative', height: 16, minWidth: schmal ? 56 : 90, opacity: grau ? 0.55 : 1 }} aria-hidden>
      <Box sx={{ position: 'absolute', left: 0, right: 0, top: 7, height: '1px', backgroundColor: viz.grid }} />
      {a !== null && b !== null && (
        <Box sx={{ position: 'absolute', left: x(Math.min(a, b)), width: `calc(${x(Math.max(a, b))} - ${x(Math.min(a, b))})`, top: 6, height: 3, backgroundColor: farbe, opacity: 0.6 }} />
      )}
      {b !== null && (
        <Box sx={{ position: 'absolute', left: x(b), top: 3, width: 10, height: 10, ml: '-5px', borderRadius: '50%', border: '2px solid', borderColor: farbe, backgroundColor: 'background.paper' }} />
      )}
      {a !== null && <Box sx={{ position: 'absolute', left: x(a), top: 3, width: 10, height: 10, ml: '-5px', borderRadius: '50%', backgroundColor: farbe }} />}
    </Box>
  );
};

/** Legende mit denselben Kreisen wie die Hantel (hohl = Vergleich, voll = jetzt); die gemeinsame `Legende` kennt nur
 *  Quadrat und Strich, ein Strich für den hohlen Kreis las sich wie eine Linie, die es nicht gibt. */
const HantelLegende: FC<{ items: { label: string; color: string; hohl?: boolean }[] }> = ({ items }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.75, mb: 1 }}>
    {items.map((i) => (
      <Box key={i.label} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 13 }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            border: '2px solid',
            borderColor: i.color,
            backgroundColor: i.hohl ? 'background.paper' : i.color,
            flex: '0 0 auto',
          }}
        />
        {i.label}
      </Box>
    ))}
  </Box>
);

const QualitaetQuelleKarte: FC = () => {
  const [dim, setDim] = useState<QualitaetDimension>('produkt');
  const [data, setData] = useState<QualitaetQuelleAntwort | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [messRef, breite] = useBreitePx();
  const schmal = breite !== null && breite < SCHMAL_UNTER;
  const viz = useVizStyle();

  useEffect(() => {
    let cancelled = false;
    setFehler(null);
    fetchQualitaetQuelle(dim)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setFehler(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [dim]);

  const aktuell = data && data.dim === dim ? data : null;
  const webinar = dim === 'webinar';
  const vergleichLabel = webinar ? TERMS.produktRest.label : 'Vorjahr';
  const ende = aktuell ? hantelEnde([aktuell.alle, ...aktuell.rows]) : 50;
  const wenigGrund = (r: QualitaetQuelleZeile) =>
    r.ohne_vergleich === 'kein_etikett'
      ? 'Webinar ohne Produktetikett: kein Vergleich, keine Marke.'
      : r.wenig
        ? `Unter ${aktuell?.schwelle ?? 20} reifen Anfos ${webinar ? 'im Webinar oder im Rest des Produkts' : 'jetzt oder im Vorjahr'} — eine Handvoll Zahler entscheidet, deshalb grau und ohne Marke.`
        : null;
  const quoteZelle = (rate: number | null, k: number, n: number, marke?: 'up' | 'down' | null) =>
    rate === null ? (
      <>—</>
    ) : (
      <Box sx={{ lineHeight: 1.25 }}>
        {formatPct(rate)}
        <AbweichungMarke marke={marke} tip={TERMS.abweichungZeitraumMarke.tip} />
        <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}>
          {int(k)} von {int(n)}
        </Box>
      </Box>
    );

  const spalten: Spalte<QualitaetQuelleZeile>[] = [
    {
      key: 'quelle',
      label: REITER.find((x) => x.key === dim)?.label ?? 'Quelle',
      sortValue: (r) => r.label,
      render: (r) => (
        <Box sx={{ lineHeight: 1.3, whiteSpace: 'normal', minWidth: 0 }}>
          {r.label}
          {r.zusatz && (
            <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}>
              {r.zusatz}
            </Box>
          )}
        </Box>
      ),
    },
    {
      key: 'jetzt',
      label: 'Bezahlquote',
      einheit: webinar ? 'Webinar' : 'jetzt',
      tip: TERMS.bezahlquote.tip,
      align: 'right',
      unsicher: wenigGrund,
      sortValue: (r) => r.pay_rate,
      render: (r) => (
        <Box sx={{ lineHeight: 1.25 }}>
          {quoteZelle(r.pay_rate, r.paid, r.n, r.test?.flag)}
          {schmal && (
            <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', whiteSpace: 'normal' }}>
              {webinar ? 'Rest' : 'VJ'} {r.pay_rate_basis === null ? '—' : formatPct(r.pay_rate_basis)}
              {r.delta_pp === null ? '' : ` · ${pp(r.delta_pp)}`}
            </Box>
          )}
        </Box>
      ),
    },
    ...(schmal
      ? []
      : ([
          {
            key: 'basis',
            label: vergleichLabel,
            tip: webinar ? TERMS.produktRest.tip : TERMS.qualitaetJeQuelle.tip,
            align: 'right',
            unsicher: wenigGrund,
            sortValue: (r) => r.pay_rate_basis,
            render: (r) => quoteZelle(r.pay_rate_basis, r.paid_basis, r.n_basis),
          },
          {
            key: 'delta',
            label: 'Unterschied',
            einheit: 'Prozentpunkte',
            tip: `Bezahlquote ${webinar ? 'des Webinars minus Rest des Produkts' : 'jetzt minus Vorjahr'}, in Prozentpunkten. ${TERMS.abweichungZeitraumMarke.tip}`,
            align: 'right',
            unsicher: wenigGrund,
            sortValue: (r) => r.delta_pp,
            render: (r) => pp(r.delta_pp),
          },
        ] as Spalte<QualitaetQuelleZeile>[])),
    {
      key: 'hantel',
      label: `${webinar ? 'Rest' : 'Vorjahr'} ○ → ● ${webinar ? 'Webinar' : 'jetzt'}`,
      einheit: `0–${ende} %`,
      tip: 'Die Hantel zeigt beide Bezahlquoten auf derselben Achse; die Farbe nennt die Richtung nur, wenn der Unterschied auffällig ist (▲ grün, ▼ rot). Sortiert nach dem Unterschied.',
      unsicher: wenigGrund,
      sortValue: (r) => r.delta_pp,
      // 390 px: mit 90 px lief die Tabelle 30 px über und schnitt die Punkte rechts ab (gemessen 29.10.).
      minWidth: schmal ? 56 : 140,
      render: (r) => <Hantel r={r} ende={ende} grau={!!wenigGrund(r)} schmal={schmal} />,
    },
  ];

  const alle = aktuell?.alle;
  return (
    <ChartCard
      title="Zahlen die Anfos einer Quelle schlechter als früher?"
      anker="qualitaet-je-quelle"
      howToRead="Bezahlquote der Anfos, die heute 60 bis 240 Tage alt sind, je Produkt, Kanal oder Webinar — der hohle Kreis ist der Vergleich, der volle die Quote jetzt. Produkt und Kanal vergleichen mit denselben Tagen ein Jahr früher, ein Webinar mit dem Rest seines Produkts. ▲/▼ nur, wo der Unterschied auch nach Korrektur für die vielen Zeilen nicht Zufall ist."
      actions={
        <ToggleButtonGroup size="small" exclusive value={dim} onChange={(_, v) => v && setDim(v)} aria-label="Quelle">
          {REITER.map((x) => (
            <ToggleButton key={x.key} value={x.key} sx={{ textTransform: 'none', py: 0.25 }}>
              {x.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      }
      terms={[
        { label: TERMS.qualitaetJeQuelle.label, tip: TERMS.qualitaetJeQuelle.tip },
        { label: TERMS.bezahlquote.label, tip: TERMS.bezahlquote.tip },
        { label: TERMS.abweichungZeitraumMarke.label, tip: TERMS.abweichungZeitraumMarke.tip },
        ...(webinar ? [{ label: TERMS.produktRest.label, tip: TERMS.produktRest.tip }] : []),
        ...(dim === 'kanalgruppe' ? [{ label: TERMS.kanalGruppe.label, tip: TERMS.kanalGruppe.tip }] : []),
        { label: TERMS.vorlaeufig.label, tip: TERMS.vorlaeufig.tip },
      ]}
      source={webinar ? 'PL-X-Bestellungen und Zahlungen, Webinar-Zuordnung der Bestellungen' : 'PL-X-Bestellungen und Zahlungen, Kennung der Bestellung'}
      freshnessKey={webinar ? ['plx_orders', 'webinar_zuordnung'] : 'plx_orders'}
    >
      <Box ref={messRef}>
        {!aktuell || !alle ? (
          <Laden fehler={fehler} />
        ) : (
          <>
            <BefundZeile
              text={
                <>
                  Alle reifen Anfos zahlen zu {formatPct(alle.pay_rate)} gegen {formatPct(alle.pay_rate_basis)} im Vorjahr ({ppText(alle.delta_pp, 'lang')}
                  {alle.test?.flag ? ', auffällig' : ', im Rahmen des Zufalls'}).{' '}
                  {(() => {
                    const auff = aktuell.rows.filter((r) => r.test?.flag);
                    return auff.length > 0
                      ? `Auffällig ${webinar ? 'gegen den Rest ihres Produkts' : 'gegen das Vorjahr'}: ${auff.map((r) => `${r.label} ${r.test?.flag === 'up' ? '▲' : '▼'}`).join(', ')}.`
                      : `Keine ${REITER.find((x) => x.key === dim)?.label ?? 'Quelle'}-Zeile weicht auffällig ab (geprüft über alle ${int(aktuell.familie)} Zeilen zugleich).`;
                  })()}
                </>
              }
              tip={TERMS.abweichungZeitraumMarke.tip}
            />
            <Kartenkopf>
              Bestellungen {spanne(aktuell.fenster.jetzt.von, letzterTag(aktuell.fenster.jetzt.bis))} (60–240 Tage alt)
              {webinar ? '' : ` gegen ${spanne(aktuell.fenster.vorjahr.von, letzterTag(aktuell.fenster.vorjahr.bis))}`} · Stand{' '}
              <DatenstandText ds={aktuell.datenstand} />
            </Kartenkopf>
            <HantelLegende
              items={[
                { label: webinar ? 'Rest des Produkts' : 'Vorjahr', color: viz.series1, hohl: true },
                { label: webinar ? 'Webinar' : 'jetzt', color: viz.series1 },
                { label: '▲ auffällig höher', color: '#2e7d32' },
                { label: '▼ auffällig niedriger', color: '#c62828' },
                { label: 'grau: zu wenige Anfos', color: viz.muted },
              ]}
            />
            <SortableTable
              rows={aktuell.rows}
              columns={spalten}
              rowKey={(r) => r.key}
              maxHeight={webinar ? 640 : null}
              eng={schmal}
              muted={(r) => !!wenigGrund(r)}
              mutedLast
              initialSort={{ key: 'jetzt', dir: 'desc' }}
              vergleichsZeilen={[{ ...alle, label: webinar ? 'Alle Anfos (gegen Vorjahr)' : 'Alle Anfos' }]}
            />
            <Hinweis>
              Nur reife Anfos (mindestens {aktuell.reife_tage} Tage alt), ohne vorläufig ergänzte. Unter {aktuell.schwelle} reifen Anfos auf einer
              Seite grau und ohne Marke. ▲/▼ nur, wo der Unterschied auch dann nicht nach Zufall aussieht, wenn alle{' '}
              {int(aktuell.familie)} prüfbaren Zeilen dieses Reiters zugleich verglichen werden (Rechenweg: „Wie diese Sektion rechnet“ auf der Übersicht).
              {webinar
                ? ' Ein Webinar zählt alle ihm zugeordneten Anfos; verglichen wird mit allen übrigen Anfos seines Produktetiketts im selben Zeitraum. Webinare ohne Etikett (Mehrprodukt-Formate) haben keinen Vergleich. Die Zeile „Alle Anfos“ vergleicht mit dem Vorjahr.'
                : ''}
              {dim === 'produkt' ? ' Produkte, die es nur in einem der beiden Zeiträume gab, stehen grau.' : ''}
            </Hinweis>
          </>
        )}
      </Box>
    </ChartCard>
  );
};

export default QualitaetQuelleKarte;
